// Updated CampManager with popup forms and participant cards
import { useEffect, useState } from 'react';
import axios from 'axios';
import CampForm from './CampForm';
import ParticipantForm from './ParticipantForm';
import styles from '../default.module.css';
import stylesSpec from './campManager.module.css';
import { usePermissionGuard } from '../../adminPanelTools';
import ParticipantOverview from './ParticipantsOverview';
import { downloadCSV, formatDateGerman, formatBoolean } from '../../../../code/DownloadHandler';
import { showInfoToast, showWarningToast, showErrorToast } from '../../../../code/ToastHelper';
import ParticipantCard from './ParticipantCard';
import { Icons } from '../../../../code/IconHandler';
export default function CampManager() {
    const checking = usePermissionGuard('handle_camps');
    const [camps, setCamps] = useState([]);
    const [campToEdit, setCampToEdit] = useState(undefined);
    const [selectedCamp, setSelectedCamp] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [editParticipant, setEditParticipant] = useState(undefined);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => { fetchCamps(); }, []);
    useEffect(() => { if (selectedCamp) fetchParticipants(selectedCamp.id); }, [selectedCamp]);

    const fetchCamps = async () => {
        try {
            const res = await axios.get('/api/camp/');
            const data = Array.isArray(res.data) ? res.data : [];
            setCamps(data);
            setSelectedCamp(data.length === 1 ? data[0] : null);
            return data;
        } catch (err) { console.error('Fehler beim Laden der Camps:', err); }
    };

    const fetchParticipants = async (campId) => {
        try {
            const res = await axios.get(`/api/participant/${campId}`);
            setParticipants(res.data);
        } catch (err) {
            console.error('Fehler beim Laden der Teilnehmer:', err);
        }
    };

    const deleteParticipant = async (id) => {
        if (!window.confirm("Teilnehmer wirklich löschen?")) return;
        try {
            await axios.delete(`/api/participant/${id}`);
            fetchParticipants(selectedCamp.id);
            showWarningToast("Teilnehmer gelöscht!");
        } catch (err) {
            console.error('Fehler beim Löschen:', err);
            showErrorToast("Fehler beim Löschen des Teilnehmers aufgetreten!")
        }
    };

    const deleteCamp = async (camp) => {
        if (!camp || !window.confirm(`Möchtest du das Camp "${camp.title}" wirklich löschen?`)) return;
        try {
            await axios.delete(`/api/camp/${camp.id}`);
            setSelectedCamp(null);
            fetchCamps();
            setParticipants([]);
            showWarningToast(`Camp: ${camp.title} wurde gelöscht!`)
        } catch (err) {
            console.error("Fehler beim Löschen des Camps:", err);
            showErrorToast(`Beim Löschen des Camps: ${camp.name} ist ein Fehler aufgetreten!`)
        }
    };

    const columnMapping = [
        { key: "camp_id", exclude: true }, { key: "id", exclude: true },
        { key: "vornameKind", label: "Vorname Kind" },
        { key: "nachnameKind", label: "Nachname Kind" },
        { key: "birthdate", label: "Geburtsdatum", format: formatDateGerman },
        { key: "mitglied", label: "Mitglied", format: formatBoolean },
        { key: "email", label: "E-Mail" }, { key: "shirt", label: "T-Shirt Größe" },
        { key: "vornameErziehung", label: "Vorname Erziehungsberechtigte" },
        { key: "nachnameErziehung", label: "Nachname Erziehungsberechtigte" },
        { key: "telefon", label: "Telefonnummer" }, { key: "street", label: "Straße" },
        { key: "housenumber", label: "Hausnummer" }, { key: "zusatz", label: "Zusatz" },
        { key: "plz", label: "PLZ" }, { key: "area", label: "Ort" },
        { key: "eatingHabit", label: "Essgewohnheiten" }, { key: "allergies", label: "Allergien" },
        { key: "sickness", label: "Krankheiten" }, { key: "bemerkung", label: "Bemerkung" },
        { key: "created_at", exclude: true },
    ];

    if (checking) return <div className={styles.container}><p>Lade...</p></div>;

    return (
        <div className={styles.container}>
            <h1>Camps verwalten</h1>

            {campToEdit !== undefined && (
                <CampForm
                    camp={campToEdit}
                    onSave={async (id, action) => {
                        const updated = await fetchCamps();
                        const selected = id ? updated.find(c => c.id === id) : updated[updated.length - 1];
                        setSelectedCamp(selected || null);
                        setCampToEdit(undefined);
                        showInfoToast(`Camp ${selected?.title || 'Unbekannt'} wurde ${action === 'create' ? 'erstellt' : 'geupdatet'}!`);
                    }}
                    onClose={() => setCampToEdit(undefined)}
                />
            )}

            {editParticipant !== undefined && (
                <ParticipantForm
                    participant={editParticipant}
                    camp_id={selectedCamp.id}
                    onClose={() => {
                        setEditParticipant(undefined);
                        fetchParticipants(selectedCamp.id);
                    }}
                />
            )}

            {camps.length > 0 && (
                <div className={stylesSpec.camps}>
                    <div className={stylesSpec.choiceCamp}>
                        <label>Camp auswählen:</label>
                        <select className={stylesSpec.selectItem} value={selectedCamp?.id || ''} onChange={(e) => {
                            const camp = camps.find(c => c.id.toString() === e.target.value);
                            setSelectedCamp(camp);
                        }}>
                            {!selectedCamp && <option value="">-- Camp auswählen --</option>}
                            {camps.map(c => (<option key={c.id} value={c.id}>{c.title}</option>))}
                        </select>
                        {selectedCamp && (
                            <>
                                <div className={stylesSpec.buttons}>
                                    <button className={stylesSpec.editButton} disabled={!selectedCamp} onClick={() => setCampToEdit(selectedCamp)}><Icons.Edit /></button>
                                    <button className={stylesSpec.deleteButton} disabled={!selectedCamp} onClick={() => deleteCamp(selectedCamp)}><Icons.Delete /></button>
                                </div>
                            </>
                        )}
                    </div>

                    <button className={stylesSpec.addButton} onClick={() => setCampToEdit(null)}><Icons.Add /> Neues Camp erstellen</button>
                </div>
            )}

            {selectedCamp && (
                <>
                    <div className={stylesSpec.teilnehmerDiv}>
                        <h2>Teilnehmer</h2>
                        <input
                            type="text"
                            placeholder="Nach Teilnehmer suchen..."
                            className={stylesSpec.searchInput}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button className={stylesSpec.addButton} onClick={() => setEditParticipant(null)}>
                            <Icons.Add /> Teilnehmer hinzufügen
                        </button>
                    </div>
                    <div className={stylesSpec.participantGrid}>
                        {participants
                            .filter(p =>
                                `${p.vornameKind} ${p.nachnameKind}`.toLowerCase().includes(searchTerm.toLowerCase())
                            )
                            .sort((a, b) => {
                                const nameA = `${a.vornameKind} ${a.nachnameKind}`.toLowerCase();
                                const nameB = `${b.vornameKind} ${b.nachnameKind}`.toLowerCase();
                                return nameA.localeCompare(nameB, 'de');
                            })
                            .map(p => (
                                <ParticipantCard
                                    key={p.id}
                                    participant={p}
                                    onEdit={() => setEditParticipant(p)}
                                    onDelete={() => deleteParticipant(p.id)}
                                />
                            ))}
                        {participants.length === 0 && <p>Keine Teilnehmer</p>}
                    </div>

                    {participants.length != 0 && (
                        <>
                            <ParticipantOverview participants={participants} />

                            <div className={styles.centerDiv}>
                                <button onClick={() => downloadCSV(participants, selectedCamp, columnMapping)} className={styles.greenButton}>
                                    ⬇️ Teilnehmer als CSV herunterladen
                                </button>
                            </div>
                        </>
                    )}

                </>
            )}
        </div>
    );
}